import type { ReactNode } from "react";
import { Alert as MantineAlert } from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

type AlertTone = "danger" | "warn" | "info";

const COLORS: Record<AlertTone, string> = {
  danger: "danger",
  warn: "warn",
  info: "brand",
};

const ICONS = {
  danger: IconAlertCircle,
  warn: IconAlertTriangle,
  info: IconInfoCircle,
} as const;

interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  className?: string;
  /**
   * Whether the message interrupts. True for anything that appears in
   * response to an action; false for a note that is simply part of the page,
   * which would otherwise be announced as an alert every time the form
   * renders and would make every real alert harder to find.
   */
  announce?: boolean;
}

/** The prefix keeps the meaning readable without the colour. */
export function Alert({
  tone = "danger",
  title,
  children,
  className,
  announce = true,
}: AlertProps) {
  const { t } = useTranslation("common");
  const Icon = ICONS[tone];
  const fallbackTitle =
    tone === "danger" ? t("alertError") : tone === "warn" ? t("alertWarning") : t("alertInfo");

  return (
    <MantineAlert
      color={COLORS[tone]}
      title={title ?? fallbackTitle}
      icon={<Icon size={20} stroke={1.75} aria-hidden />}
      className={cn(className)}
      role={announce ? "alert" : "note"}
      variant="light"
    >
      {children}
    </MantineAlert>
  );
}
